import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box, Container, Paper, Typography, Button, Skeleton, Alert, Tooltip, Tabs, Tab,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { useUser } from '../context/UserContext';
import { BACKEND_URL } from '../utils/constants';
import { colors } from '../theme';
import DashboardMetrics from './DashboardMetrics';
import DashboardCompare from './DashboardCompare';

// --------------------------------------------------------------------------- //
// Types — mirror the GET /pipeline/analytics payload (DPL-004).
// --------------------------------------------------------------------------- //
interface FunnelStage {
  stage: string;
  color: string | null;
  entered: number;
  current: number;
  conversion_to_next: number | null;
}
interface TimeInStage { stage: string; median_days: number | null; avg_days: number | null; }
interface VelocityPoint { month: string; created_count: number; closed_count: number; }
interface NamedCount { name: string; count: number; }
interface StateCount { state: string; count: number; }
interface Mix {
  by_model_type: NamedCount[];
  by_state: StateCount[];
  irr_buckets: Record<string, number>;
}
interface Summary {
  total_active: number;
  total_closed: number;
  close_rate: number | null;
  median_days_to_close: number | null;
}
interface Analytics {
  pipeline_configured: boolean;
  funnel: FunnelStage[];
  time_in_stage: TimeInStage[];
  velocity: VelocityPoint[];
  mix: Mix;
  summary: Summary;
}

const CHART_PALETTE = [
  colors.blue, colors.success, '#D97706', '#7C3AED', '#EC4899',
  '#06B6D4', '#EAB308', '#EF4444', colors.navy, '#14B8A6',
];

// --------------------------------------------------------------------------- //
// Terse formatters
// --------------------------------------------------------------------------- //
const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const fmtCount = (n: number | null | undefined) => (n == null ? '—' : compact.format(n));
const fmtPct = (ratio: number | null | undefined) =>
  ratio == null ? '—' : `${Math.round(ratio * 100)}%`;
const fmtDays = (d: number | null | undefined) =>
  d == null ? '—' : `${Math.round(d)}d`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (key: string) => {
  // 'YYYY-MM' -> "Jun" (or "Jan '26" at year boundaries for context)
  const [y, m] = key.split('-');
  const idx = parseInt(m, 10) - 1;
  const name = MONTHS[idx] ?? key;
  return idx === 0 ? `${name} '${y.slice(2)}` : name;
};

// Readable text color for a user-chosen stage background (WCAG luminance).
const readableText = (bg: string | null | undefined): string => {
  if (!bg) return colors.grey[900];
  const hex = bg.replace('#', '');
  if (hex.length !== 3 && hex.length !== 6) return colors.grey[900];
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? colors.grey[900] : colors.white;
};

// --------------------------------------------------------------------------- //
// Module-level presentational components (kept OUT of Dashboard to avoid
// remount-on-every-render — see memory: inline component anti-pattern).
// --------------------------------------------------------------------------- //
const StatCard: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <Paper variant="outlined" sx={{ p: 2.5 }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{label}</Typography>
    <Typography className="num" sx={{ fontSize: '1.875rem', fontWeight: 400, color: colors.navy, lineHeight: 1.1 }}>
      {value}
    </Typography>
    {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
  </Paper>
);

// `fill` sets an explicit height:100% — only for cards whose BODY must grow
// (a chart that fills the card). Cards in a stretch-aligned grid row do NOT
// need it: height:auto already stretches to the row, and height:100% inside an
// auto-sized row resolves circularly and overshoots.
const SectionCard: React.FC<{ title: string; subtitle?: string; fill?: boolean; children: React.ReactNode }> = ({
  title, subtitle, fill = true, children,
}) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, height: fill ? '100%' : 'auto', display: 'flex', flexDirection: 'column' }}>
    <Typography variant="h5" sx={{ fontWeight: 700, mb: subtitle ? 0.25 : 1.5 }}>{title}</Typography>
    {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{subtitle}</Typography>}
    <Box sx={{ flex: fill ? 1 : 'none', minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</Box>
  </Paper>
);

// --------------------------------------------------------------------------- //
// Pipeline table — funnel and time-in-stage share ONE row per stage, so the
// duration bar reads as a continuation of the stage it belongs to and the stage
// name is written once. Two measures, two scales, so they never share an axis:
// the funnel bars carry the user's stage colors (categorical identity) and the
// duration bars use a single hue (magnitude), with their own baseline scale
// labelled once in the footer.
// --------------------------------------------------------------------------- //
const GRID_GAP = 2.5;
const DAYS_VALUE_W = 46;      // px reserved for the right-aligned "160d" column
const DUR_COLOR = colors.navy;

const gridCols = (showTime: boolean) => ({
  xs: '1fr',
  sm: showTime
    ? 'minmax(120px, 190px) minmax(0, 1fr) minmax(150px, 210px)'
    : 'minmax(120px, 190px) minmax(0, 1fr)',
});

const HeadCell: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
  <Typography
    variant="caption"
    sx={{
      color: colors.grey[600], fontWeight: 700, fontSize: '0.6875rem',
      textAlign: align,
    }}
  >
    {children}
  </Typography>
);

/** One stage: label + counts, funnel bar, duration bar. All vertically centred. */
const StageRow: React.FC<{
  stage: FunnelStage;
  maxEntered: number;
  maxDays: number;
  days: number | null;
  showTime: boolean;
  isLast: boolean;
}> = ({ stage, maxEntered, maxDays, days, showTime, isLast }) => {
  const color = stage.color || colors.grey[400];
  const trackPct = maxEntered > 0 ? Math.max((stage.entered / maxEntered) * 100, stage.entered > 0 ? 6 : 1.5) : 1.5;
  const fillPct = stage.entered > 0 ? Math.min(100, (stage.current / stage.entered) * 100) : 0;
  const dayPct = days != null && maxDays > 0 ? Math.max((days / maxDays) * 100, 1.5) : 0;

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridCols(showTime),
          columnGap: GRID_GAP,
          rowGap: 1,
          alignItems: 'center',
          py: 1,
          mx: { xs: 0, sm: -1.5 },
          px: { xs: 0, sm: 1.5 },
          borderRadius: 1,
          transition: 'background-color .15s ease',
          '&:hover': { bgcolor: colors.grey[50] },
        }}
      >
        {/* Stage identity */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={stage.stage}
            >
              {stage.stage}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.grey[600], whiteSpace: 'nowrap' }}>
              {fmtCount(stage.entered)} entered · {fmtCount(stage.current)} now
            </Typography>
          </Box>
        </Box>

        {/* Funnel bar — track width = deals that ever entered, fill = still here */}
        <Tooltip
          arrow
          title={`${stage.stage}: ${stage.entered} entered · ${stage.current} currently in this stage`}
        >
          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                height: 24, width: `${trackPct}%`, minWidth: 26, borderRadius: 0.75,
                bgcolor: alpha(color, 0.2), position: 'relative', transition: 'width .25s ease',
              }}
            >
              {stage.current > 0 && (
                <Box
                  sx={{
                    position: 'absolute', insetBlock: 0, left: 0, width: `${fillPct}%`,
                    bgcolor: color, borderRadius: 0.75, transition: 'width .25s ease',
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 0.75,
                  }}
                >
                  {fillPct > 22 && (
                    <Typography variant="caption" sx={{ color: readableText(color), fontWeight: 700, lineHeight: 1 }}>
                      {fmtCount(stage.current)}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Tooltip>

        {/* Duration bar — same row, own scale, value right-aligned into a column */}
        {showTime && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
            <Typography
              variant="caption"
              sx={{ display: { xs: 'block', sm: 'none' }, color: colors.grey[600], whiteSpace: 'nowrap' }}
            >
              Median in stage
            </Typography>
            <Tooltip
              arrow
              title={days == null
                ? `${stage.stage}: not enough history to measure time in stage`
                : `${stage.stage}: median ${Math.round(days)} days in stage`}
            >
              <Box sx={{ flex: 1, minWidth: 40, height: 10, borderRadius: 5, bgcolor: alpha(DUR_COLOR, 0.1), position: 'relative' }}>
                {days != null && (
                  <Box
                    sx={{
                      position: 'absolute', insetBlock: 0, left: 0, width: `${dayPct}%`,
                      bgcolor: DUR_COLOR, borderRadius: 5, transition: 'width .25s ease',
                    }}
                  />
                )}
              </Box>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                width: DAYS_VALUE_W, flexShrink: 0, textAlign: 'right', fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                color: days == null ? colors.grey[400] : colors.grey[900],
              }}
            >
              {fmtDays(days)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Conversion connector — sits under the funnel column it describes */}
      {!isLast && (
        <Box sx={{ display: 'grid', gridTemplateColumns: gridCols(showTime), columnGap: GRID_GAP }}>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: 18 }}>
            <Box sx={{ width: '2px', height: 18, borderRadius: '1px', bgcolor: colors.grey[300], ml: '10px', flexShrink: 0 }} />
            <Typography
              variant="caption"
              sx={{
                color: stage.conversion_to_next == null ? 'text.disabled' : colors.blueDark,
                fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap',
              }}
            >
              {stage.conversion_to_next == null ? '—' : `${fmtPct(stage.conversion_to_next)} to next`}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

// --------------------------------------------------------------------------- //
// Chart cards.
//
// Every chart is given an EXPLICIT pixel height rather than being asked to fill
// a flex parent. MUI X sizes itself from a ResizeObserver, and a chart that is
// measured while its flex parent is still 0-height keeps that height through
// later window resizes — which is what left the velocity chart squashed at the
// top of an over-tall card. Explicit heights + `fill={false}` cards means the
// card always hugs the chart, at any viewport width.
// --------------------------------------------------------------------------- //
const CHART_H = 300;
const AXIS_SX = {
  '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: colors.grey[300] },
  '& .MuiChartsAxis-tickLabel': { fill: colors.grey[600], fontSize: 12 },
  '& .MuiChartsGrid-line': { stroke: colors.grey[300], strokeDasharray: '3 3' },
} as const;

const VelocityCard: React.FC<{ velocity: { months: string[]; created: number[]; closed: number[] } }> = ({ velocity }) => (
  <SectionCard title="Velocity" subtitle="Deals created vs. closed over the last 12 months." fill={false}>
    {velocity.months.length > 0 ? (
      <Box sx={{ width: '100%', ...AXIS_SX }}>
        <LineChart
          height={CHART_H}
          xAxis={[{ scaleType: 'point', data: velocity.months }]}
          yAxis={[{ min: 0 }]}
          series={[
            { data: velocity.created, label: 'Created', color: colors.blue, curve: 'monotoneX', showMark: true },
            { data: velocity.closed, label: 'Closed', color: colors.success, curve: 'monotoneX', showMark: true },
          ]}
          grid={{ horizontal: true }}
          margin={{ left: 4, right: 12, top: 8, bottom: 4 }}
        />
      </Box>
    ) : <EmptyChartNote text="No activity in the last 12 months." />}
  </SectionCard>
);

/** Donut + its own legend list, so long model-type names can't squeeze the ring. */
const ModelTypeCard: React.FC<{ slices: { id: number; value: number; label: string; color: string }[]; total: number }> = ({
  slices, total,
}) => {
  const sum = slices.reduce((a, s) => a + s.value, 0) || 1;
  return (
    <SectionCard title="By model type" subtitle={`${total} deal${total === 1 ? '' : 's'} across your portfolio.`} fill={false}>
      {slices.length > 0 ? (
        <>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <PieChart
              height={220}
              width={220}
              hideLegend
              series={[{
                data: slices,
                highlightScope: { fade: 'global', highlight: 'item' },
                valueFormatter: (item) => `${item.value} deal${item.value === 1 ? '' : 's'}`,
                innerRadius: 58,
                outerRadius: 104,
                paddingAngle: 1.5,
                cornerRadius: 3,
              }]}
              margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
            />
          </Box>
          <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2.5, rowGap: 1 }}>
            {slices.map((s) => (
              <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={s.label}
                >
                  {s.label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                  {s.value}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.grey[600], width: 38, textAlign: 'right', flexShrink: 0 }}>
                  {Math.round((s.value / sum) * 100)}%
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      ) : <EmptyChartNote text="No model types yet." />}
    </SectionCard>
  );
};

/**
 * Ranked bar list rather than a MUI bar chart: state labels and counts stay
 * legible at every card width (a band axis drops ticks when it gets narrow, and
 * in-bar value labels spill outside short bars), and it matches the funnel
 * table's visual language.
 */
const TopStatesCard: React.FC<{ labels: string[]; values: number[] }> = ({ labels, values }) => {
  const max = Math.max(1, ...values);
  return (
    <SectionCard title="Top states" subtitle="Where your deals are." fill={false}>
      {labels.length > 0 ? (
        <Box sx={{ mt: 0.5 }}>
          {labels.map((label, i) => (
            <Tooltip key={label} arrow title={`${label}: ${values[i]} deal${values[i] === 1 ? '' : 's'}`}>
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, py: 0.85,
                  mx: -1.5, px: 1.5, borderRadius: 1,
                  transition: 'background-color .15s ease',
                  '&:hover': { bgcolor: colors.grey[50] },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ width: 46, flexShrink: 0, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {label}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 40, height: 18, borderRadius: 0.75, bgcolor: alpha(colors.navy, 0.08), position: 'relative' }}>
                  <Box
                    sx={{
                      position: 'absolute', insetBlock: 0, left: 0,
                      width: `${Math.max((values[i] / max) * 100, 2)}%`,
                      bgcolor: colors.navy, borderRadius: 0.75, transition: 'width .25s ease',
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{ width: 34, flexShrink: 0, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                >
                  {values[i]}
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      ) : <EmptyChartNote text="No location data yet." />}
    </SectionCard>
  );
};

const PipelineTable: React.FC<{
  funnel: FunnelStage[];
  daysByStage: Record<string, number | null>;
  maxEntered: number;
  maxDays: number;
  showTime: boolean;
}> = ({ funnel, daysByStage, maxEntered, maxDays, showTime }) => (
  <Box>
    {/* Column headers — the duration column is labelled once, not per row */}
    <Box
      sx={{
        display: { xs: 'none', sm: 'grid' },
        gridTemplateColumns: gridCols(showTime),
        columnGap: GRID_GAP,
        alignItems: 'end',
        pb: 1,
        borderBottom: `1px solid ${colors.grey[300]}`,
      }}
    >
      <HeadCell>Stage</HeadCell>
      <HeadCell>Deals</HeadCell>
      {showTime && <HeadCell align="right">Median days in stage</HeadCell>}
    </Box>

    <Box sx={{ mt: 1 }}>
      {funnel.map((s, i) => (
        <StageRow
          key={s.stage}
          stage={s}
          maxEntered={maxEntered}
          maxDays={maxDays}
          days={daysByStage[s.stage] ?? null}
          showTime={showTime}
          isLast={i === funnel.length - 1}
        />
      ))}
    </Box>

    {/* Baseline scale for the duration bars — replaces a per-chart y-axis */}
    {showTime && (
      <Box
        sx={{
          display: { xs: 'none', sm: 'grid' },
          gridTemplateColumns: gridCols(showTime),
          columnGap: GRID_GAP,
          mt: 1.25, pt: 1, borderTop: `1px solid ${colors.grey[300]}`,
        }}
      >
        <Box />
        <Box />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: colors.grey[600] }}>0</Typography>
            <Typography variant="caption" sx={{ color: colors.grey[600] }}>{fmtDays(maxDays)}</Typography>
          </Box>
          <Box sx={{ width: DAYS_VALUE_W, flexShrink: 0 }} />
        </Box>
      </Box>
    )}
  </Box>
);

// --------------------------------------------------------------------------- //
// Page
// --------------------------------------------------------------------------- //
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useUser();

  // Tab lives in the query string so a metrics view is linkable and survives
  // a refresh. 'pipeline' is the default and carries no param.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab = tabParam === 'metrics' || tabParam === 'compare' ? tabParam : 'pipeline';

  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
      });
      const r = await fetch(`${BACKEND_URL}/api/pipeline/analytics`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`Request failed (${r.status})`);
      setData(await r.json());
    } catch (e: any) {
      setError(e?.message || 'Could not load analytics.');
    } finally {
      setLoading(false);
    }
  }, [user, getAccessTokenSilently]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  // ---- Derived chart data (memoized so charts don't recompute per render) ----
  const totalDeals = data ? data.summary.total_active + data.summary.total_closed : 0;
  const maxEntered = useMemo(
    () => (data ? Math.max(1, ...data.funnel.map((f) => f.entered)) : 1),
    [data],
  );
  // Time-in-stage keyed by stage name so each funnel row can render its own
  // duration bar; `maxDays` is the shared baseline for those bars.
  const { daysByStage, maxDays, hasTiming } = useMemo(() => {
    const map: Record<string, number | null> = {};
    let max = 0;
    (data?.time_in_stage ?? []).forEach((t) => {
      map[t.stage] = t.median_days;
      if (t.median_days != null) max = Math.max(max, t.median_days);
    });
    return { daysByStage: map, maxDays: max, hasTiming: max > 0 };
  }, [data]);
  const velocity = useMemo(() => {
    if (!data) return { months: [] as string[], created: [] as number[], closed: [] as number[] };
    return {
      months: data.velocity.map((v) => monthLabel(v.month)),
      created: data.velocity.map((v) => v.created_count),
      closed: data.velocity.map((v) => v.closed_count),
    };
  }, [data]);
  const typePie = useMemo(
    () => (data ? data.mix.by_model_type.map((t, i) => ({
      id: i, value: t.count, label: t.name, color: CHART_PALETTE[i % CHART_PALETTE.length],
    })) : []),
    [data],
  );
  const stateBars = useMemo(() => {
    if (!data) return { labels: [] as string[], values: [] as number[] };
    const top = data.mix.by_state.slice(0, 8);
    return { labels: top.map((s) => s.state), values: top.map((s) => s.count) };
  }, [data]);

  // ----------------------------------- render ----------------------------------- //
  return (
    <Container maxWidth={false} sx={{ maxWidth: 1280, width: '100%', mt: { xs: 2, sm: 4 }, mb: 6, px: { xs: 1.5, sm: 3 } }}>
      <Typography variant="h4" sx={{ fontSize: '1.875rem', fontWeight: 700, mb: 0.5 }}>Dashboard</Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
        {tab === 'metrics'
          ? 'Return, pricing and size distributions across your saved models.'
          : tab === 'compare'
            ? 'Put deals side by side on the metrics that decide them.'
            : 'Conversion, velocity, and portfolio insights across your deals.'}
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setSearchParams(v === 'pipeline' ? {} : { tab: v }, { replace: true })}
        sx={{
          mb: 3, minHeight: 42, borderBottom: `1px solid ${colors.grey[300]}`,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 42, px: 0, mr: 3 },
        }}
      >
        <Tab value="pipeline" label="Pipeline" />
        <Tab value="metrics" label="Deal metrics" />
        <Tab value="compare" label="Comparison" />
      </Tabs>

      {tab === 'metrics' && <DashboardMetrics />}
      {tab === 'compare' && <DashboardCompare />}

      {tab === 'pipeline' && (
        <>
        {loading && <LoadingSkeleton />}

        {!loading && error && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={fetchAnalytics}>Retry</Button>}>
            {error}
          </Alert>
        )}

        {!loading && !error && data && totalDeals === 0 && (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>No deals yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first model to start tracking your pipeline.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/create-model')}>Create a model</Button>
          </Paper>
        )}

        {!loading && !error && data && totalDeals > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Stat cards */}
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
              <StatCard label="Active deals" value={fmtCount(data.summary.total_active)} />
              <StatCard label="Closed" value={fmtCount(data.summary.total_closed)} />
              <StatCard label="Close rate" value={fmtPct(data.summary.close_rate)} hint="of all deals" />
              <StatCard label="Median days to close" value={fmtDays(data.summary.median_days_to_close)} />
            </Box>

            {/* Funnel + time in stage */}
            {data.pipeline_configured ? (
              <SectionCard
                title="Conversion funnel"
                subtitle={hasTiming
                  ? 'Deals that entered each stage, where they sit now, and how long they take.'
                  : 'Deals that entered each stage, and where they sit now.'}
                fill={false}
              >
                <PipelineTable
                  funnel={data.funnel}
                  daysByStage={daysByStage}
                  maxEntered={maxEntered}
                  maxDays={maxDays}
                  showTime={hasTiming}
                />
                {!hasTiming && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                    Stage timing appears once deals have moved between stages.
                  </Typography>
                )}
              </SectionCard>
            ) : (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Set up your pipeline to see conversion rates</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Define and order your deal stages, then tag your deals to unlock the funnel and time-in-stage insights.
                </Typography>
                <Button variant="contained" onClick={() => navigate('/settings')}>Set up pipeline</Button>
              </Paper>
            )}

            {/* Velocity */}
            <VelocityCard velocity={velocity} />

            {/* Mix row — cards stretch to a shared row height so "Top states"
                lines up with the taller donut card instead of ending short. */}
            <Box sx={{ display: 'grid', gap: 3, alignItems: 'stretch', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
              <ModelTypeCard slices={typePie} total={totalDeals} />
              <TopStatesCard labels={stateBars.labels} values={stateBars.values} />
            </Box>
          </Box>
        )}
        </>
      )}
    </Container>
  );
};

const EmptyChartNote: React.FC<{ text: string }> = ({ text }) => (
  <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Typography variant="body2" color="text.secondary">{text}</Typography>
  </Box>
);

const LoadingSkeleton: React.FC = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
      {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={104} />)}
    </Box>
    <Skeleton variant="rounded" height={340} />
    <Skeleton variant="rounded" height={320} />
    <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
      {[0, 1].map((i) => <Skeleton key={i} variant="rounded" height={380} />)}
    </Box>
  </Box>
);

export default Dashboard;
